import {
  ContentLoader, Input,
  Message, MessageType, TimelineVariant, Typography,
} from "@trussle/external-components";
import React from "react";
import "./informationPanel.scss";
import { StaticImage } from "gatsby-plugin-image";
import { Control, Controller } from "react-hook-form";
import { IRateWatchPanelFormValues } from "./index";

interface IInformationPanelProps {
    cheapestRepaymentLoading: boolean;
    cheapestRepayment: {
        initialPeriodRate: number;
        monthlyRepayment: number;
    }
    variant: TimelineVariant;
    control: Control<IRateWatchPanelFormValues, any>;
}

export const InformationPanel: React.FC<IInformationPanelProps> = ({
  cheapestRepayment, cheapestRepaymentLoading, variant, control,
}) => {
  if (variant === TimelineVariant.Late) {
    return (
      <div className="rate-watch-information-panel rate-watch-information-panel-late">
        <div className="rate-watch-information-panel__top">
          <div className="rate-watch-information-panel__top-left">
            <Typography variant="h2" classes="rate-watch-information-panel__title">Act Now to Secure a Better Rate</Typography>
            <p className="rate-watch-information-panel__body">
              Time's ticking, but better rates are still within reach.
              Avoid paying a high SVR by submitting your remortgage today.
            </p>
          </div>
          <div className="rate-watch-information-panel__top-right">
            <StaticImage
              src="../../MortgageComparisonWidget/img/better-logo.svg"
              alt="Better.co.uk logo"
              className="rate-watch-information-panel__top-right__logo"
              width={166}
            />
          </div>
        </div>
        <Message type={MessageType.INFORMATION}>
          <div className="rate-watch-information-panel__message">
            <p className="rate-watch-information-panel__message__title">Tracker Mortgage versus SVR</p>
            <p className="rate-watch-information-panel__message__body">
              Tracker mortgages move up and down relative to the Bank of England interest rate,
              so the amount you pay may vary month on Month.
              However, trackers are almost always better value than an SVR (Standard Variable Rate)
              that you will default to when your current mortgage offer runs out.
              Trackers also often have no ERC, so when you&apos;re ready to lock into a fixed term you won&apos;t be penalised for it.
            </p>
          </div>
        </Message>
      </div>
    );
  }

  if (variant === TimelineVariant.OnTime) {
    return (
      <div className="rate-watch-information-panel rate-watch-information-panel-on-time">
        <div className="rate-watch-information-panel__top">
          <div className="rate-watch-information-panel__top-left">
            <Typography variant="h2" classes="rate-watch-information-panel__title">Your Window to Unlock a Better Rate</Typography>
            <p className="rate-watch-information-panel__body">
              You're now within the six-month period to lock in a favourable remortgage rate.
              Our expert advisors are here to help you secure the lowest rate available.
              View historic trends to see where you stand.
            </p>
          </div>
          <div className="rate-watch-information-panel__top-right">
            <StaticImage
              src="../../MortgageComparisonWidget/img/better-logo.svg"
              alt="Better.co.uk logo"
              className="rate-watch-information-panel__top-right__logo"
              width={166}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-watch-information-panel rate-watch-information-panel-on-time">
      <div className="rate-watch-information-panel__top">
        <div className="rate-watch-information-panel__top-left">
          <Typography variant="h2" classes="rate-watch-information-panel__title">Plan ahead and secure lower rates</Typography>
          <p className="rate-watch-information-panel__body">
            Sign up to receive Mortgage RateWatch email updates to your inbox.
            You can apply for a remortgage once your current deal has six months left.
          </p>
        </div>
        <div className="rate-watch-information-panel__top-right">
          <StaticImage
            src="../../MortgageComparisonWidget/img/better-logo.svg"
            alt="Better.co.uk logo"
            className="rate-watch-information-panel__top-right__logo"
            width={166}
          />
        </div>
      </div>
      <div className="rate-watch-information-panel__bottom-rates">
        <div>
          <p className="rate-watch-information-panel__bottom-deals">We currently have deals available at:</p>
          {cheapestRepaymentLoading ? (
            <ContentLoader classOverrides={{
              contentLoader: "rate-watch-panel__bottom-section__loading__results",
            }}
            />
          ) : (
            <p className="rate-watch-information-panel__bottom-repayment">
              £
              {cheapestRepayment.monthlyRepayment}
              {" "}
              <span className="rate-watch-information-panel__bottom-initial-period">
                (based on
                {" "}
                {cheapestRepayment.initialPeriodRate}
                {" "}
                rate and 2 year fixed)
              </span>
            </p>
          )}

        </div>
        <div>
          <Controller
            control={control}
            name="currentTerm"
            render={({
              field: {
                onChange, onBlur, value, name, ref,
              },
            }) => (
              <Input
                label="Mortgage term (years)"
                value={value!}
                name={name}
                type="number"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                id={name}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};
